/**
 * What a notification says somebody should be told about.
 *
 * Deliberately NOT the same list as `ACTIVITY_TYPES`, and the two must not be
 * merged. An activity is a fact about a record and its audience is a fan-out
 * set; a notification is addressed to one named recipient and carries per-reader
 * read state. Several facts are one and not the other in both directions:
 * `provenance.added` is an activity with no single recipient, and an affiliation
 * claim is a notification with no record to be an activity about — see the
 * comment at the top of `feedsFor` in the API's `activity.events.ts`.
 *
 * Mirrors no Postgres enum, for the reason `ACTIVITY_TYPES` gives: these live in
 * Stream, which is a view of the history rather than the history. So this list
 * is the whole contract, versioned only by deployment, and readers must tolerate
 * a type they have never heard of rather than fail on it. `isNotificationType`
 * is that tolerance.
 *
 * Ordered by how much they want from the reader: answer something, hear back
 * about something you asked, then news.
 */
export const NOTIFICATION_TYPES = [
    "verification.requested",
    "verification.referred",
    "verification.answered",
    "affiliation.claimed",
    "affiliation.answered",
    "work.registered",
    "collection.shared",
    /**
     * Somebody named you as the agent for a work, or ended that.
     *
     * News rather than an ask: nothing is being requested and there is nothing to
     * answer. The removal is here for the reason the assignment is — an agent who
     * simply found the work gone from their workspace would have no way to tell a
     * revocation from a bug.
     */
    "agent.assigned",
    "agent.removed",
    /**
     * Your appointment is about to run out, or it has.
     *
     * BOTH PARTIES ARE TOLD, AND THAT IS THE POINT RATHER THAN A CONVENIENCE.
     * OND-76: "Both sides are warned before a term bites -- equal responsibility,
     * so neither party can claim they were not told." So one lapse produces two
     * notifications, addressed to the agent and to whoever appointed them, and
     * `agent.assigned`'s rule that "a notification about your own act is noise"
     * does not apply: a lapse is nobody's act.
     *
     * `expiring` ARRIVES THREE TIMES -- one calendar month, one week and one day
     * before -- and carries `when` to say which. Three sendings of one verb
     * rather than three verbs, because they tell the same party the same thing
     * with a different number in it; 20261003093000 decision 1's rule is that
     * kinds diverge where the PROJECTIONS diverge, and these do not.
     *
     * Deduplicated per mandate, per term and per warning in
     * `ondo.work_agent_mandate_warnings`, so a replayed timer tells nobody twice
     * and a renewal mints a fresh set of three.
     *
     * NEWS RATHER THAN AN ASK, both of them. There is an act available on an
     * expiring appointment -- either side may renew it alone -- but it is not an
     * ANSWER to anything, and nothing is waiting on the reader. Putting it in
     * `ASKS` would leave a row outstanding on an inbox until a term the reader may
     * be perfectly content to let lapse.
     */
    "mandate.expiring",
    "mandate.expired",
    /**
     * Something significant changed about an agent you appointed — OND-107.
     *
     * ADDRESSED TO THE CLIENT AND NEVER TO THE AGENT, which is the one way these
     * differ from every other verb in the appointment block above. `agent.*` and
     * `mandate.*` reach the agent, or both parties; these reach
     * `ondo.work_agent_mandates.assigned_by_principal_id` — the person who made
     * the appointment — because the agent's own standing changing is the agent's
     * own news, and `agent.assigned`'s rule that "a notification about your own
     * act is noise" applies to the firm that ended the seat as well.
     *
     * THREE VERBS BECAUSE THE INBOX RENDERS COPY PER VERB AND NOTHING ELSE.
     * `NotificationEntry` carries no payload field, so a discriminating value
     * cannot reach the screen — `mandate.expiring`'s `when` never does — and
     * OND-107 requires the message to use the change's ACTUAL status: "an
     * unconfirmed claim must not be described as a confirmed move." A single verb
     * with the status in its payload would have put that sentence somewhere no
     * reader ever sees it.
     *
     * The two organisation verbs split where the ACCESS consequence splits, not
     * where `ondo.membership_status` does. 'active' is `joined`; 'ended' and
     * 'suspended' are both `left`, because `ondo.principal_ids_at_least()` reads
     * `status = 'active'` and a suspended member reaches exactly as much as a
     * departed one — which is why the copy says "no longer an active member" and
     * never "has left". An 'invited' membership raises nothing at all: an
     * invitation nobody has taken up changes no access and is not a move.
     * 20261004092000 decision 8.
     *
     * `authority_changed` is the scope notification `20261003099000` decision 17
     * deferred to this ticket by name. It fires when somebody OTHER than the
     * appointing party changes what an appointment reaches — a gallery or a
     * delegator, which `ondo.mandate_scope_authority` admits — and never when an
     * owner changes their own.
     *
     * NONE OF THE THREE IS IN `ASKS`, and none of them is close. There is nothing
     * to answer: the client is being told to review a scope they already
     * recorded, and reviewing is not an act with an outstanding row behind it.
     *
     * NEITHER THE AGENT'S NEW EMPLOYER NOR ANY ORGANISATION IS NAMED. The firm
     * that reaches the work is the one named on the appointment, which has not
     * changed and is on the record under a policy; the firm the agent moved to
     * reaches nothing and is a third party's employment. 20261004092000
     * decision 16.
     *
     * Deduplicated per change, appointment and recipient on
     * `ondo.agent_change_notices`, so processing one recorded change twice tells
     * nobody twice. There is no activity verb beside any of them: the work is
     * represented by the same agent on the same terms as it was a moment ago, and
     * only the agent's standing moved — the call `20260927100000` decision 7 made
     * of a delegation and `20261003096000` made of a renewal.
     */
    "agent.organisation_joined",
    "agent.organisation_left",
    "agent.authority_changed",
    /**
     * An agent asked to represent your work, and you answered them.
     *
     * `requested` is an ASK — see the list below — and it is the first one in this
     * vocabulary that arrives from somebody the reader has no relation to at all.
     * That is bounded in SQL rather than here: `ondo.claim_probe()` caps the door
     * at five refusals an hour, so an inbox cannot be filled by walking the code
     * space.
     *
     * `answered` goes back to the agent, and it REPLACES `agent.assigned` on this
     * path rather than joining it. An agent who asked and was granted would
     * otherwise be told twice about one act, and the sentence that names the thing
     * they did — "your request was granted" — is the more useful of the two.
     */
    "representation.requested",
    "representation.answered",
    /**
     * A work was offered to your gallery, you answered, or an arrangement ended.
     *
     * `offered` is an ASK — a gallery has to decide whether to take the picture —
     * and it is the second door in this vocabulary that opens from somebody the
     * reader may have no relation to. It is bounded exactly as
     * `representation.requested` is, and by the same mechanism rather than a
     * second one: `ondo.offer_consignment()` runs through
     * `ondo.principal_eligibility()`, so `ondo.claim_probe()` caps the refusals.
     *
     * `answered` goes back to the consignor, and `ended` to whichever party did
     * not do the ending. That last one exists for the reason `agent.removed`
     * does: a gallery whose inventory simply lost a row would have no way to tell
     * a recall from a bug.
     */
    "consignment.offered",
    "consignment.answered",
    "consignment.ended",
    /**
     * The other party proposed new terms, or answered the ones you proposed.
     *
     * TWO VERBS WHERE THE ACTIVITY VOCABULARY HAS ONE, which is this pair of lists
     * doing what they are for. A trail records the moment the terms CHANGED,
     * because that is the fact about the arrangement; an inbox has to carry the
     * proposal as well, because a proposal addressed to somebody who is never told
     * about it is a negotiation conducted by telephone — which is the thing the
     * registry exists to prevent.
     *
     * `revision_proposed` goes to whichever party did NOT propose, and it plainly
     * wants an answer. IT IS DELIBERATELY NOT IN `ASKS`, for
     * `consignment.offered`'s reason word for word: "A gallery answers a
     * consignment on its INVENTORY screen, which reads ondo.gallery_inventory() —
     * the live rows — so the place that shows what is outstanding already reads the
     * truth." A pending revision is the same shape of fact, read from
     * `ondo.consignment_revisions_for()`, and making this an ask would add a second
     * reader of it and a second chance for the two to disagree.
     *
     * `revision_answered` goes back to the party who proposed, on an acceptance and
     * on a refusal alike — one verb for both, because what the reader does next is
     * the same thing: open the record, which says which it was. That is
     * `loan.ended`'s rule, and the reason it does not follow the activity
     * vocabulary's split here.
     */
    "consignment.revision_proposed",
    "consignment.revision_answered",
    /**
     * Somebody offered a price for a work you are party to, the agreed floor
     * refused one, or the owner answered yours — OND-44.
     *
     * THREE VERBS, AND THE MIDDLE ONE IS THE TICKET. `offer.refused` is the only
     * notification in this list sent on behalf of nobody: an offer below the
     * effective agreed floor is refused in the statement that records it, because
     * the owner already said what they would not go below. It goes to all three
     * parties at once — the buyer, the owner and the gallery — which is OND-44's
     * "the rejection is visible to every party to the work at the same time", and
     * the reason it is not folded into `offer.answered`: that one tells two
     * parties about a person's decision, and this one tells three parties that
     * nobody had to make one.
     *
     * THE SEPARATION IS WHAT MAKES THE OWNER'S SCREEN HONEST. Without it the owner
     * learns nothing when a lowball arrives, and "no interest" and "interest at
     * the wrong price" look identical from the inbox.
     *
     * NONE OF THE THREE IS IN `ASKS`, for `consignment.revision_proposed`'s reason
     * word for word: the screen that shows what is outstanding reads the live rows
     * — `ondo.offers_for()`, where `yours_to_answer` is the same predicate the
     * write door is bounded by — and an `awaiting` computed from a feed would be a
     * second reader of the same fact and a second chance for the two to disagree.
     *
     * AND NONE OF THEM CARRIES THE FIGURE. They name the work and the offer; the
     * price is on a row under a policy. `20261003097000` decision 11.
     */
    "offer.received",
    "offer.refused",
    "offer.answered",
    /**
     * A loan was proposed to you, answered, or ended.
     *
     * THREE WHERE THE ACTIVITY VOCABULARY HAS FIVE, and the difference is the
     * clearest illustration of why these two lists are not one. An activity is a
     * fact about a record, so a proposal withdrawn and an arrangement returned are
     * different sentences on a trail. A notification is addressed to one party, so
     * what matters is who is waiting — and the party told that a loan ENDED does
     * not need to know from the verb whether it was unsaid, cancelled or handed
     * back. They open the record, which says.
     *
     * `loan.proposed` goes to the borrower, and it is the only one of the three
     * that arrives from somebody the reader may have no relation to at all. It is
     * bounded the way every such door is rather than by a mechanism of its own:
     * `ondo.propose_loan()` resolves the borrower's ONDO id inside a definer that
     * already requires owner rights, and gives one opaque refusal.
     *
     * IT IS DELIBERATELY NOT IN `ASKS`, and the absence is the lesson
     * `settlement.remitted` left behind two paragraphs below: it was listed as an
     * ask before the API could read whether it was still open, so it never once
     * rendered as awaiting. A loan proposal plainly wants an answer — but accept
     * and decline are OND-9's routes and do not exist yet, and nothing in this API
     * can ask Postgres whether a proposal addressed to the reader is still
     * outstanding. It becomes an ask in the ticket that builds the screen where it
     * is answered, with the definer that answers it, and not before.
     *
     * `loan.answered` goes to the party who was waiting — the lender, when a
     * borrower assents. `loan.ended` goes to whichever party did NOT end it, which
     * is `consignment.ended`'s rule exactly, and it exists for the reason
     * `agent.removed` does: a museum whose loan simply vanished from their
     * workspace would have no way to tell a recall from a bug. Its grant is
     * revoked in the same statement that ends the loan, so this is the last
     * notification about that work they can be shown at all.
     */
    "loan.proposed",
    "loan.answered",
    "loan.ended",
    /**
     * A work has been sold to you, or you have answered a sale.
     *
     * `transfer.offered` points at the WORK, which is somewhere the reader can
     * actually be sent: an unanswered offer of title opens
     * `ondo.transfer_offered_work_ids()`, so a buyer can look at the picture
     * before deciding. It arrives from somebody they may have no relation to, and
     * is bounded the way every such door is rather than by a mechanism of its own
     * — `ondo.effect_sale()` charges `ondo.claim_probe()` on a refused buyer id.
     *
     * IT IS IN `ASKS`, and it took two pieces to get there. The collector's
     * `offers` destination reads `ondo.my_transfer_offers()` and lists what the
     * reader has been offered title to — that is where the answer lives. And
     * `NotificationsReadService` now reads the same definer to decide `awaiting`:
     * a `transfer.offered` row is open while its work is still in that set, and
     * not a moment longer. No new target kind was needed — the target stays
     * `work:<id>`, which discloses nothing — because "is this ask still open" is
     * a question about the reader's offers and not about the transfer row.
     *
     * `transfer.answered` goes back to the gallery that effected the sale — the
     * party waiting to hear whether title landed — and to nobody else. The seller
     * is not told: they learn it from the work leaving their collection, which is
     * a fact rather than a message.
     *
     * `transfer.withdrawn` goes the other way, to the buyer, and exists for the
     * reason `agent.removed` and `consignment.ended` do. An offer of title that
     * simply stopped appearing would give a buyer no way to tell a retraction
     * from a bug — and this is the last notification about that work that can be
     * rendered at all, because withdrawing closes
     * `ondo.transfer_offered_work_ids()` and the picture becomes unreadable in
     * the same statement.
     */
    "transfer.offered",
    "transfer.answered",
    "transfer.withdrawn",
    /**
     * A sale you recorded could not be completed, and has been reversed.
     *
     * THE ONLY VERB IN THIS VOCABULARY RAISED BY ONDO ITSELF rather than by
     * somebody's act, and that is what makes it a separate type rather than a
     * `transfer.withdrawn` sent the other way. `withdrawn` is the effecting party
     * telling the buyer they have retracted a sale; this is the registry telling
     * the effecting party that it retracted one on their behalf, which is a
     * sentence nobody should have to infer from a verb that means the opposite.
     *
     * It is the last step of a saga rollback — OND-45's "the final step of that
     * rollback alerts the person who started it, so a half-finished sale is
     * something somebody is told about rather than something discovered later".
     * News rather than an ask: there is nothing to answer, and what to do next
     * (record the sale again) is a fresh act on a screen they already know.
     *
     * It carries a REASON, which no other verb in this list does. Every other
     * notification points at a record the reader can open to find out more; this
     * one describes something that happened inside ONDO, where there is no such
     * record and the sentence is all there is. The reason is written by ONDO, not
     * by a party, so it discloses nothing about anybody.
     */
    "sale.reversed",
    /**
     * The buyer you recorded off ONDO says they did not buy it — OND-103.
     *
     * ONE VERB WHERE THE CLAIM FLOW HAS FOUR EVENTS, and the three silences are
     * the decision. An invitation going out is machinery. A confirmation moves the
     * transfer to 'accepted', so `transfer.answered` goes out from the sale's own
     * engine and this flow must not say the same thing twice. A sale withdrawn
     * under an unanswered claim already has `transfer.withdrawn`. What is left is
     * the one state nothing else can announce: a dispute changes NOTHING about the
     * row, so there is no transition for any other verb to ride on.
     *
     * IT GOES TO THE SELLER AND THE SELLING FIRM, which is one party more than
     * `transfer.answered` tells. That verb can leave the seller out because they
     * "learn it from the work leaving their collection, which is a fact rather
     * than a message"; here nothing leaves anything. The work is still sold, still
     * in the seller's collection, still not theirs to sell again — and a dispute
     * is not a fact anybody can read off the object.
     *
     * News rather than an ask, and that is a judgement rather than an omission:
     * there is no door that answers a dispute. OND-103 says any resolution is
     * "recorded through the normal append-only provenance mechanism", which is a
     * position on the entry, taken on the work's own record. It becomes an ask in
     * the ticket that builds a screen where it is answered, with the definer that
     * says whether it is still open — the order `settlement.remitted` learned the
     * hard way.
     *
     * It carries NO REASON, where `sale.reversed` directly above carries prose.
     * That one is ONDO's own sentence about its own failure with no record behind
     * it; this one is a party's accusation with a record behind it, and the
     * accusation is read beside the sale it is about, under a policy.
     */
    "claim.disputed",
    /**
     * The selling firm proposed documents or a correction for a sale's history
     * line, your answer was collected, or the addition became part of the record.
     *
     * THREE VERBS AND NO ACTIVITY VERB AT ALL, which is the sharpest case in this
     * file of the two vocabularies diverging. OND-71's confirmed follow-up makes
     * an addition after title moves a thing EVERY recorded sale party has to
     * approve, so every beat of it is addressed to named parties — and the work's
     * trail is deliberately told none of it, because `work:W` is read by everyone
     * who may read the work and a proposal is correspondence between the parties
     * to one sale. `consignment.revision_proposed`'s argument, and a stronger
     * version of it: a document's LABEL is an invoice in miniature.
     *
     * `addition_proposed` goes to each party who owes an answer. It is
     * deliberately NOT in `ASKS`, for `consignment.revision_proposed`'s reason
     * word for word: the screen that shows what is outstanding reads the live
     * rows — `ondo.sale_additions_for()` — and an `awaiting` computed from a feed
     * would be a second reader of the same fact.
     *
     * `addition_answered` goes back to the proposing firm when a party REFUSES,
     * and `addition_approved` when the last approval lands. Two verbs where the
     * revision has one, because here the two outcomes are not the same news: a
     * refusal leaves the firm holding an invoice with nowhere to put it, and an
     * approval means the record now says something it did not. What the reader
     * does next differs, which is this list's own test.
     *
     * NONE OF THE THREE CARRIES A LABEL, A FILENAME OR THE CORRECTED TEXT. They
     * say that an addition exists and which version it is; what it says is read
     * through a definer bounded by being a party. That is the policy OND-108 is
     * reviewing, and a notification that quoted the document would have decided
     * the question before it was asked.
     */
    "sale.addition_proposed",
    "sale.addition_answered",
    "sale.addition_approved",
    /**
     * A post-sale covenant of yours fell due, or its money moved.
     *
     * `settlement.due` is how a gallery LEARNS AT ALL. It is not a party to the
     * resale that triggered its covenant — `transfers_read` admits the buyer, the
     * seller and the effecting firm, and the covenant holder is none of them — so
     * without this the only way to find out is to open the screen and look. It
     * goes to the gallery, and to the artist when the covenant elected them.
     *
     * `settlement.remitted` goes to the ARTIST, and it is an ASK rather than
     * news: it says a gallery has recorded paying you, and invites you to confirm
     * or dispute it. That is the whole of decision 4 of
     * 20260906090000_resale_settlements.sql — without an answer, a settlement
     * would be the only place in this vocabulary where somebody is recorded as
     * paid without ever being asked. Not sent when the artist holds no ONDO id,
     * because there is nobody to send it to.
     *
     * `settlement.answered` goes back to the gallery with the artist's verdict,
     * which is `verification.answered`'s shape exactly.
     */
    "settlement.due",
    "settlement.remitted",
    "settlement.answered",
    /**
     * Somebody has put an authorship claim naming your artist up for
     * confirmation — OND-77.
     *
     * NEWS, AND IT IS NOT THE SAME NEWS AS `verification.requested`. That verb
     * goes to the party ASKED and is a question waiting for them; this goes to the
     * estate the work is ATTRIBUTED to, which was not asked and has nothing to
     * answer. The two never both fire for one principal: the recipient here is
     * excluded when it is the party asked, in SQL, inside the transaction — see
     * `attributionNoticeOndoId` in `ondo.verification_projection()`.
     *
     * IT IS NOT RAISED BY A REGISTRATION, and that is the whole of OND-77's
     * reconciliation with OND-20: "creating a work or naming its artist creates no
     * studio activity; only an explicit attribution-confirmation request reaches
     * the studio." The ticket's older "every registration" trigger is superseded.
     * So nine works typed into the registry naming Erwin Wurm raise nothing at
     * all; what raises this is somebody asking a party of their own choosing to
     * put its name to one of those claims.
     *
     * NOT AN ASK. The estate cannot answer it — `ondo.answerable_verification_
     * request_ids()` admits the party asked and nobody else — and there is no read
     * anywhere that could say whether it is still open FOR THEM. This list's own
     * rule applies: "a type listed in `ASKS` before the API can read whether it is
     * still open never renders as awaiting — the mistake `settlement.remitted`
     * recorded."
     *
     * It names the WORK, which the recipient can read by construction:
     * `works_read`'s second arm IS `ondo.works.attributed_principal_id`, and that
     * column is who this is addressed to. It does not name the entry, which the
     * recipient cannot read until somebody asks them.
     */
    "attribution.claimed",
    /**
     * Somebody took a position on an entry you wrote, unasked.
     *
     * Last because it is the purest news in the list: nothing is being asked, no
     * question the reader raised has come back, and there is nothing to do. It
     * belongs here rather than under `verification.answered` because that one is
     * the reply to a request the reader made, and this one answers nothing —
     * there was no request. See `ondo.attest_provenance`, which decides who is
     * told and declines to tell an author they agreed with themselves.
     */
    "verification.attested",
    /**
     * The studio wants more evidence before it answers, has revised its target
     * date, or has given your case to one of its own people.
     *
     * THREE VERBS AND NOT ONE, which is the opposite of what the outbox does with
     * them — `verification.serviced` is one event KIND carrying all three, and
     * `serviceSteps` branches on the payload. The divergence is deliberate and
     * runs the right way round: an event kind is about which run owes what, and a
     * notification TYPE is a sentence somebody reads. "The Wurm studio needs two
     * more photographs", "it will now answer by 20 November" and "Tomas, this is
     * yours" have three audiences between them and nothing in common but the
     * case they are about.
     *
     * THE FIRST TWO ARE ASKS AND ARE NOT IN `ASKS`, and the absence is the rule
     * that list keeps rather than an oversight. `activity.ts` put it exactly: "a
     * type listed in `ASKS` before the API can read whether it is still open
     * never renders as awaiting". Whether more evidence is still wanted is not a
     * fact the registry holds — the schema records that the studio asked, and
     * records nothing about what the collector then sent, because what counts as
     * enough is the studio's judgement and not a column. So these read as news
     * with something to do about it, which is the truth.
     *
     * `verification.assigned` IS NEWS AND ONLY NEWS, for `agent.assigned`'s
     * reason and with a sharper edge: being handed a case gives the reader
     * nothing to answer WITH. OND-60: "Assignment never grants an unqualified
     * person artist authority or the ability to sign."
     */
    "verification.evidence_requested",
    "verification.extended",
    "verification.assigned",
];
export function isNotificationType(value) {
    return (typeof value === "string" &&
        NOTIFICATION_TYPES.includes(value));
}
/**
 * The ones that are a question rather than news.
 *
 * A notification of one of these types is `awaiting` while the thing it points
 * at is still open — whether it IS still open is a fact the API reads from
 * Postgres, never from the feed. This list only says which types can be.
 */
const ASKS = [
    "verification.requested",
    "verification.referred",
    "affiliation.claimed",
    "representation.requested",
    // The artist has been told a gallery says it paid them, and the whole point
    // of telling them is that they may say otherwise. It is `awaiting` until
    // `ondo.resale_settlements.artist_answered_at` is set — a fact the API reads
    // from Postgres, never from the feed. IT WAS LISTED HERE BEFORE THE API
    // COULD READ IT: the `work` branch of `resolve` answered `open: false` for
    // every row, so this never once read as awaiting. It does now, through
    // `ondo.artist_settlements()`.
    "settlement.remitted",
    // Title has been offered to the reader and they have not answered. Open
    // while the work is still in `ondo.my_transfer_offers()`.
    "transfer.offered",
    // A loan has been proposed to the reader and they have not answered. Open
    // while the loan is still 'proposed' in `ondo.borrower_loans()`.
    //
    // IT WAS DELIBERATELY ABSENT UNTIL OND-9, and the absence is worth reading
    // because it is the rule this list keeps. `activity.ts` said why: "a type
    // listed in `ASKS` before the API can read whether it is still open never
    // renders as awaiting — the mistake `settlement.remitted` recorded." A
    // proposal plainly wanted an answer, and there was no route to answer it with
    // and no read that could say whether it stood.
    //
    // `20261003094000_borrower_loan_lifecycle.sql` built both, so this value
    // arrives in the same commit as `ondo.borrower_loans()` and the accept and
    // decline doors — never before them.
    "loan.proposed",
];
/** Whether this type can be waiting on the reader at all. */
export function isAsk(type) {
    return ASKS.includes(type);
}
/**
 * How a person has asked to be told — per kind of notification.
 *
 * THIS LIST DOES MIRROR A POSTGRES ENUM, which is the opposite of what
 * `NOTIFICATION_TYPES` above says about itself, so the difference is worth
 * stating rather than looking like an inconsistency. A notification TYPE is a
 * Stream verb: it lives in a feed store that is a view of the history, it is
 * versioned by deployment, and a reader must tolerate one it has never heard
 * of. A delivery MODE is the domain of a column in
 * `ondo.notification_preferences` — the database is the record for it, as it is
 * for everything a person chose — so it is `ondo.notification_delivery`, and
 * this array must stay in step with that enum. Nothing enforces that but this
 * comment and `pnpm test:rls`.
 *
 * There are three because the ticket asked for three, and there is no fourth
 * hiding here: "immediately" and "in the daily digest" are both delivery, and
 * "off" is the absence of it rather than a third place to put something.
 */
export const NOTIFICATION_DELIVERIES = ["immediate", "digest", "off"];
export function isNotificationDelivery(value) {
    return (typeof value === "string" &&
        NOTIFICATION_DELIVERIES.includes(value));
}
/**
 * What a person who has never opened the preferences screen gets.
 *
 * ON, for every kind, which is the whole of "sensible defaults on, so nobody
 * has to configure anything to be told the important things". It is also why
 * `ondo.notification_preferences` holds only DEPARTURES from this: a person
 * with no rows is not unconfigured, they are the default, and a kind added by a
 * later deployment is on for everybody without a backfill.
 *
 * `ondo.delivery_for_notification()` is the authority at delivery time and
 * states the same default in SQL. This copy exists so the preferences screen
 * can render a choice for a kind nobody has a row for; if the two ever
 * disagree, the database wins and the screen is lying.
 */
export const DEFAULT_NOTIFICATION_DELIVERY = "immediate";
/**
 * When a daily digest is composed, for a principal who has not said otherwise.
 *
 * NINE O'CLOCK, IN UTC, AND THE SAME HOUR EVERYWHERE — the owner's answer of 28
 * September 2026 in terms: "Default delivery is 09:00 UTC for everyone. The
 * person's editable send time is also expressed in UTC; label it clearly. This
 * is one global clock, not 09:00 in each reader's local timezone."
 *
 * `ondo.notification_digest_hour()` is the authority and states the same default
 * in SQL, for the reason `DEFAULT_NOTIFICATION_DELIVERY` has a copy here at all:
 * `ondo.notification_digest_settings` holds only DEPARTURES, so a screen
 * rendering the control for somebody with no row needs to know what the absence
 * means. If the two ever disagree, the database wins and the screen is lying.
 *
 * AN HOUR AND NOT A TIME, which `20261005099300` decision 1 argues: a digest is
 * a daily summary, a minute is a precision nobody asked for, and it would
 * multiply the schedule's firings by sixty for a difference no reader could
 * perceive.
 */
export const DEFAULT_DIGEST_HOUR_UTC = 9;
/**
 * The hours a digest may be sent at. Twenty-four of them, 0 to 23, in UTC.
 *
 * A LIST RATHER THAN A RANGE CHECK, so the control on the screen and the Zod
 * schema at the door read the same vocabulary — the arrangement
 * `NOTIFICATION_DELIVERIES` has, and the reason `NOTIFICATION_TYPES` is a list
 * too. The database bounds the column as well, with a check constraint, because
 * that is where the column lives.
 */
export const DIGEST_HOURS_UTC = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11,
    12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
];
//# sourceMappingURL=notifications.js.map